import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Download, Award, Building2, Globe, Phone, Mail, MapPin, Target, Eye, Heart,
} from "lucide-react";
import { SITE, PARTNERS, CLIENTS, VALUES, SERVICES } from "../data/site";
import SectionHeader from "../components/SectionHeader";

// Dynamic years since founding — increases automatically each year
function yearsInBusiness() {
  const founded = SITE.yearStarted; // 2009
  const current = new Date().getFullYear();
  return current - founded;
}

export default function Profile() {
  const years = yearsInBusiness();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white">
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="container-st py-20 sm:py-28 relative grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-400">Company Profile</p>
            <h1 className="mt-4 text-5xl sm:text-6xl font-bold tracking-tighter leading-[1.05]">
              {SITE.company}
            </h1>
            <p className="mt-5 text-xl text-blue-200 italic">"{SITE.tagline}"</p>
            <p className="mt-5 max-w-2xl text-slate-300 leading-relaxed">
              An indigenous dynamic software solutions company committed to building the best and
              simplified Web, Mobile &amp; Social applications at an affordable cost. We help our
              clients run their businesses easily and effectively with scalable architectures,
              cloud deployments, SEO/SEM and application maintenance.
            </p>
            <p className="mt-3 text-sm text-slate-400">{SITE.rc}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.profilePdf}
                target="_blank"
                rel="noreferrer"
                data-testid="profile-pdf-download"
                className="btn-shine inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-6 py-3.5 rounded-full"
              >
                <Download className="w-4 h-4" /> Download PDF Profile
              </a>
              <Link
                to="/contact?type=quote"
                data-testid="profile-cta-quote"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-full"
              >
                Get a Job Quote
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-8">
              <img
                src={SITE.logo}
                alt="Amcblast Solutions Limited"
                className="w-full h-auto object-contain max-h-32 bg-slate-900 rounded-2xl p-3"
              />
              <div className="mt-6 grid grid-cols-2 gap-3 text-center">
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="text-3xl font-bold">{years}+</div>
                  <div className="text-xs uppercase tracking-widest text-slate-300">Years</div>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="text-3xl font-bold">12</div>
                  <div className="text-xs uppercase tracking-widest text-slate-300">Specialists</div>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="text-3xl font-bold">240+</div>
                  <div className="text-xs uppercase tracking-widest text-slate-300">Projects</div>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="text-3xl font-bold">{SERVICES.length}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-300">Services</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MISSION VISION VALUES */}
      <section className="section-pad">
        <div className="container-st grid lg:grid-cols-3 gap-6">
          {[
            { Icon: Target, t: "Mission", d: "To provide companies with a personal and pro-active approach to maintaining and improving their IT infrastructure and software requirements." },
            { Icon: Eye, t: "Vision", d: "Our vision serves as the framework for our roadmap — describing what we need to do to continue to be an innovative leader setting the pace." },
            { Icon: Heart, t: "Philosophy", d: "Build a complete product for any given industry through extensive research, expert involvement, and consistency through standardization." },
          ].map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-3xl border border-slate-100 bg-white p-8"
            >
              <div className="h-12 w-12 rounded-2xl bg-blue-50 grid place-items-center">
                <Icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">{t}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="section-pad bg-slate-50/60">
        <div className="container-st">
          <SectionHeader eyebrow="Core Values" title="What we believe and how we work." center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-3xl bg-white p-7 border border-slate-100"
              >
                <h4 className="text-xl font-semibold text-slate-900">{v.t}</h4>
                <p className="mt-2 text-sm text-slate-600">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS / TOOLS */}
      <section className="section-pad">
        <div className="container-st">
          <SectionHeader
            eyebrow="Accreditations"
            title="Developed in partnership with visionary companies."
            subtitle="Engineered with enterprise-grade infrastructure to ensure maximum uptime and data protection."
            center
          />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {PARTNERS.map((p) => (
              <div
                key={p}
                data-testid={`partner-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-2xl border border-slate-100 bg-white p-4 text-center text-sm font-semibold text-slate-700 hover:border-blue-200 hover:text-blue-700 transition"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="section-pad bg-slate-50/60">
        <div className="container-st">
          <SectionHeader
            eyebrow="Clients & Customers"
            title="Trusted across healthcare, finance, education, real-estate & hospitality."
            center
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {Object.entries(CLIENTS).map(([sector, list], i) => (
              <motion.div
                key={sector}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-3xl bg-white p-7 border border-slate-100"
                data-testid={`client-sector-${sector.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-blue-600">
                  <Building2 className="w-4 h-4" /> {sector}
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {list.map((c) => (
                    <li key={c} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="section-pad">
        <div className="container-st">
          <div className="rounded-[2rem] bg-slate-950 text-white p-10 sm:p-14">
            <SectionHeader
              eyebrow="Reach Us"
              title="Three offices. One call away."
              center
            />
            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {SITE.offices.map((o) => (
                <div key={o.label} className="rounded-2xl bg-white/5 p-6">
                  <div className="text-xs uppercase tracking-widest text-blue-400 font-semibold">
                    {o.label}
                  </div>
                  <div className="mt-2 text-sm text-slate-100 flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" /> {o.line}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm">
              <a href={`tel:${SITE.phone}`} className="inline-flex items-center gap-2 hover:text-blue-400">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-2 hover:text-blue-400">
                <Mail className="w-4 h-4" /> {SITE.email}
              </a>
              <span className="inline-flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" /> {SITE.website}
              </span>
            </div>
            <div className="mt-10 flex justify-center">
              <a
                href={SITE.profilePdf}
                target="_blank"
                rel="noreferrer"
                data-testid="profile-pdf-download-bottom"
                className="btn-shine inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-full"
              >
                <Download className="w-4 h-4" /> Download Full Company Profile (PDF)
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
